import { Close } from "@mui/icons-material";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, LinearProgress, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { addMonths, format } from "date-fns";
import { ToastContent } from "../../../../../shared/components";
import { ToolbarGridFilterProposal } from "../../../../../shared/components/toolbarGridProposal/ToolbarGridFilterProposal";
import { useToast } from "../../../../../shared/hooks";
import { IInstallmentProposal, ITotalStatusInstallmentProposal, ProposalService } from "../../../../../shared/services/api/proposal/ProposalService";
import { returnMessageError } from "../../../../../shared/utils/returnMessageError";
import { InstallmentCard } from "../components/InstallmentCard";
import { ModalCancelInstallments } from "./ModalCancelInstallments";
import { ModalInstallmentDetails } from "./ModalInstallmentDetails";
import { ModalSelectInstallmentsToCancel } from "./ModalSelectInstallmentsToCancel";
import { ModalFinancialLaunch } from "./ModalFinancialLaunch";
import { checkByProfileGuard } from "../../../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../../shared/enums/profiles";
import { useProfile } from "../../../../../context/ProfileContext";
import { ModalReactivateInstallments } from "./ModalReactivateInstallments";
import { ModalSelectInstallmentsToReactivate } from "./ModalSelectInstallmentsToRevert";
import { ModalAdjustInstallmentsDueDate } from "./ModalAdjustInstallmentsDueDate";
import { ModalSelectInstallmentsToSetPaid } from "./ModalSelectInstallmentsToSetPaid";
import { ModalSetInstallmentsPaid } from "./ModalSetInstallmentsPaid";

interface ModalInstallmentsProps {
    isOpen: boolean;
    onClose: () => void;
    rows: any;
    isSuperAdmin: boolean;
}

export const ModalInstallments = ({ isOpen, onClose, rows, isSuperAdmin }: ModalInstallmentsProps) => {
    const [statusFilter, setStatusFilter] = useState("Todas");
    const [installments, setInstallments] = useState<IInstallmentProposal[]>([]);
    const [totals, setTotals] = useState<ITotalStatusInstallmentProposal>();
    const [isLoading, setIsLoading] = useState(false);
    const [sortBy, setSortBy] = useState<"parcel" | "dueDate">("parcel");

    // Cancelamento
    const [openCancelModal, setOpenCancelModal] = useState(false);
    const [cancelRow, setCancelRow] = useState<IInstallmentProposal | null>(null);
    const [openSelectBatch, setOpenSelectBatch] = useState(false);
    const [rowsToCancel, setRowsToCancel] = useState<IInstallmentProposal[]>([]);

    // Reativação
    const [openSelectBatchReactivate, setOpenSelectBatchReactivate] = useState(false);
    const [rowsToReactivate, setRowsToReactivate] = useState<IInstallmentProposal[]>([]);
    const [openModalReactivate, setOpenModalReactivate] = useState(false);

    // Ajuste de vencimento
    const [openAdjustDueDate, setOpenAdjustDueDate] = useState(false);
    const [isApplyingDueDate, setIsApplyingDueDate] = useState(false);

    // Baixar parcela
    const [openSelectBatchPaid, setOpenSelectBatchPaid] = useState(false);
    const [rowsToSetPaid, setRowsToSetPaid] = useState<IInstallmentProposal[]>([]);
    const [openModalSetPaid, setOpenModalSetPaid] = useState(false);

    // Outros
    const [selectedRowDetails, setSelectedRowDetails] = useState<IInstallmentProposal | null>(null);
    const [openModalFinancial, setOpenModalFinancial] = useState(false);

    const toast = useToast();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const { permissionLevel, isSuperAdmin: isUserSuperAdmin } = useProfile();
    const canAccessFinancial = isUserSuperAdmin || permissionLevel === 1;
    const canCancelInstallments = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.CancelInstallments);
    const canReactivateInstallments = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.ReactivateInstallments);
    const canAdjustInstallments = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.AdjustInstallments);
    const canSetInstallmentsPaid = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.SetInstallmentsPaid);
    const canLaunchFinancialValues = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.LaunchFinancialValues);

    const fetchInstallments = useCallback(() => {
        setIsLoading(true);
        Promise.all([
            rows?.id ? ProposalService.getStatusInstallmentProposal(statusFilter, rows.id) : [],
            rows?.id ? ProposalService.getTotalStatusInstallmentProposal(rows.id) : null,
        ])
            .then(([resultList, resultTotal]) => {
                setIsLoading(false);

                if (resultList instanceof Error) {
                    toast.present({ type: "error", messageOrContent: <ToastContent title="Erro" content={resultList.message} /> });
                    return;
                }

                if (resultTotal instanceof Error) {
                    toast.present({ type: "error", messageOrContent: <ToastContent title="Erro" content={resultTotal.message} /> });
                    return;
                }

                setInstallments(resultList);
                setTotals(resultTotal || undefined);
            })
            .catch((err) => {
                setIsLoading(false);
                const { titleMessage, contentMessage } = returnMessageError(err);
                toast.present({ type: "error", messageOrContent: <ToastContent title={titleMessage} content={contentMessage} /> });
            });
    }, [statusFilter, rows?.id]);

    useEffect(() => {
        if (isOpen) {
            fetchInstallments();
        }
    }, [isOpen, statusFilter]);

    const handleCancel = (row: IInstallmentProposal) => {
        setCancelRow(row);
        setOpenCancelModal(true);
    };

    const handleView = (row: IInstallmentProposal) => {
        setSelectedRowDetails(row);
    };

    const handleApplyDueDate = async ({ adjustments }: { adjustments: Array<{ id: string; monthsDelta: number; amount?: string }> }) => {
        try {
            setIsApplyingDueDate(true);

            const payloads = adjustments
                .map(({ id, monthsDelta, amount }) => {
                    const installment = installments.find((i) => i.id === id);
                    if (!installment) return null;
                    if ((installment.status ?? "").toLowerCase() !== "pending") return null;

                    const baseDate = new Date(installment.dueDate + " 03:00:00");
                    const baseDueDate = format(baseDate, "yyyy-MM-dd");
                    const newDueDate = monthsDelta !== 0 ? format(addMonths(baseDate, monthsDelta), "yyyy-MM-dd") : baseDueDate;
                    const newAmount = amount ?? installment.amount;
                    return {
                        id: installment.id,
                        amount: newAmount,
                        dueDate: newDueDate,
                    };
                })
                .filter(Boolean) as Array<{ id: string; amount: string; dueDate: string }>;

            if (payloads.length === 0) {
                toast.present({
                    type: "warning",
                    messageOrContent: <ToastContent title="Atenção" content="Nenhuma parcela pendente selecionada para ajuste." />,
                });
                return;
            }

            const results = await Promise.all(payloads.map((p) => ProposalService.updateInstallmentDueDate(p)));
            const firstError = results.find((r) => r instanceof Error) as Error | undefined;
            if (firstError) {
                toast.present({ type: "error", messageOrContent: <ToastContent title="Erro" content={firstError.message} /> });
                return;
            }

            toast.present({
                type: "success",
                messageOrContent: <ToastContent title="Sucesso" content={`Vencimento atualizado para ${payloads.length} parcela(s).`} />,
                position: "top-right",
            });

            setOpenAdjustDueDate(false);
            fetchInstallments();
        } catch (err) {
            const { titleMessage, contentMessage } = returnMessageError(err);
            toast.present({ type: "error", messageOrContent: <ToastContent title={titleMessage} content={contentMessage} /> });
        } finally {
            setIsApplyingDueDate(false);
        }
    };

    return (
        <>
            <Dialog open={isOpen} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
                    <Typography fontWeight={400} variant="h6" color="primary.main">
                        Extrato de Garantia
                    </Typography>
                    <IconButton onClick={onClose}>
                        <Close />
                    </IconButton>
                </DialogTitle>

                <Box
                    sx={{
                        px: 6,
                        py: 1,
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        backgroundColor: "background.paper",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <ToolbarGridFilterProposal
                        onClickAll={() => setStatusFilter("Todas")}
                        onClickActive={() => setStatusFilter("active")}
                        onClickCanceled={() => setStatusFilter("canceled")}
                        selectAll={statusFilter === "Todas"}
                        selectAtive={statusFilter === "active"}
                        selectCanceled={statusFilter === "canceled"}
                        textButtonAll={`Todas (${totals?.total ?? 0})`}
                        textButtonActive={`Ativas (${totals?.ativo ?? 0})`}
                        textButtonCanceled={`Canceladas (${totals?.cancelada ?? 0})`}
                        onCancelBatch={canCancelInstallments ? () => setOpenSelectBatch(true) : undefined}
                        onReactivateBatch={canReactivateInstallments ? () => setOpenSelectBatchReactivate(true) : undefined}
                        onAdjustDueDateBatch={canAdjustInstallments ? () => setOpenAdjustDueDate(true) : undefined}
                        onSetPaidBatch={canSetInstallmentsPaid ? () => setOpenSelectBatchPaid(true) : undefined}
                        onLaunchValues={canLaunchFinancialValues ? () => setOpenModalFinancial(true) : undefined}
                        sortBy={sortBy}
                        onChangeSort={(value) => setSortBy(value)}
                    />
                </Box>

                <DialogContent dividers sx={{ maxHeight: "80vh", px: 6, py: 2 }}>
                    <Stack spacing={2}>
                        {isLoading && <LinearProgress />}
                        {!isLoading &&
                            [...installments]
                                .sort((a, b) => {
                                    if (sortBy === "parcel") {
                                        return (Number(a.number) || 0) - (Number(b.number) || 0);
                                    }
                                    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
                                })
                                .map((item) => (
                                    <InstallmentCard
                                        key={item.id}
                                        row={item}
                                        isSuperAdmin={isSuperAdmin}
                                        onCancel={() => handleCancel(item)}
                                        onView={handleView}
                                    />
                                ))}
                    </Stack>
                </DialogContent>
            </Dialog>

            {/* Modal cancelamento */}
            {openCancelModal && (
                <ModalCancelInstallments
                    isOpenModal={openCancelModal}
                    isClose={() => {
                        setOpenCancelModal(false);
                        setCancelRow(null);
                        setRowsToCancel([]);
                    }}
                    isSuperAdmin={isSuperAdmin}
                    row={cancelRow ?? undefined}
                    rows={rowsToCancel.length > 0 ? rowsToCancel : undefined}
                    onSuccess={() => fetchInstallments()}
                />
            )}

            {/* Modal visualização detalhes */}
            {selectedRowDetails && (
                <ModalInstallmentDetails open={Boolean(selectedRowDetails)} onClose={() => setSelectedRowDetails(null)} row={selectedRowDetails} />
            )}

            {/* Seleção em lote para cancelar */}
            {openSelectBatch && (
                <ModalSelectInstallmentsToCancel
                    installments={installments.filter((i) => i.status === "pending")}
                    onClose={() => setOpenSelectBatch(false)}
                    onConfirm={(selectedIds) => {
                        const selectedRows = installments.filter((r) => selectedIds.includes(r.id));
                        setRowsToCancel(selectedRows);
                        setOpenSelectBatch(false);
                        setOpenCancelModal(true);
                    }}
                />
            )}

            {/* Seleção em lote para reativar */}
            {openSelectBatchReactivate && (
                <ModalSelectInstallmentsToReactivate
                    installments={installments.filter((i) => i.status === "canceled")}
                    onClose={() => setOpenSelectBatchReactivate(false)}
                    onConfirm={(selectedIds) => {
                        const selectedRows = installments.filter((r) => selectedIds.includes(r.id));
                        setRowsToReactivate(selectedRows);
                        setOpenSelectBatchReactivate(false);
                        setOpenModalReactivate(true);
                    }}
                />
            )}

            {/* Modal confirmação reativação */}
            {openModalReactivate && (
                <ModalReactivateInstallments
                    isOpenModal={openModalReactivate}
                    isClose={() => {
                        setOpenModalReactivate(false);
                        setRowsToReactivate([]);
                    }}
                    isSuperAdmin={isSuperAdmin}
                    rows={rowsToReactivate}
                    onSuccess={() => fetchInstallments()}
                />
            )}

            {/* Ajustar vencimento */}
            {openAdjustDueDate && (
                <ModalAdjustInstallmentsDueDate
                    isOpen={openAdjustDueDate}
                    onClose={() => setOpenAdjustDueDate(false)}
                    installments={installments}
                    onApply={handleApplyDueDate}
                />
            )}

            {/* Seleção em lote para baixar */}
            {openSelectBatchPaid && (
                <ModalSelectInstallmentsToSetPaid
                    installments={installments}
                    onClose={() => setOpenSelectBatchPaid(false)}
                    onConfirm={(selectedIds) => {
                        const selectedRows = installments.filter((r) => selectedIds.includes(r.id));
                        setRowsToSetPaid(selectedRows);
                        setOpenSelectBatchPaid(false);
                        setOpenModalSetPaid(true);
                    }}
                />
            )}

            {/* Modal confirmação baixar */}
            {openModalSetPaid && (
                <ModalSetInstallmentsPaid
                    isOpenModal={openModalSetPaid}
                    isClose={() => {
                        setOpenModalSetPaid(false);
                        setRowsToSetPaid([]);
                    }}
                    rows={rowsToSetPaid}
                    onSuccess={() => fetchInstallments()}
                />
            )}

            {/* Modal financeiro */}
            {openModalFinancial && (
                <ModalFinancialLaunch
                    isOpenModal={openModalFinancial}
                    isClose={() => setOpenModalFinancial(false)}
                    isSuperAdmin={isSuperAdmin}
                    isUpdate={fetchInstallments}
                    rows={rows}
                />
            )}
        </>
    );
};
